/**
 * Subscribe to tab visibility and bfcache restore; caller supplies throttled handler.
 */

import type { MutableRefObject } from "react";

export type BrowserVisibilitySubscribeOptions = {
	/** Default true. False = BFCache `pageshow`+persisted only (file-picker safe). */
	includeVisibilityChange?: boolean;
};

export function runVisibilityRefetchIfThrottled(lastRunAtRef: MutableRefObject<number>, throttleMs: number, fn: () => void): void {
	const now = Date.now();
	if (now - lastRunAtRef.current < throttleMs) {
		return;
	}
	lastRunAtRef.current = now;
	fn();
}

export function shouldListenVisibilityChange(options?: BrowserVisibilitySubscribeOptions): boolean {
	return options?.includeVisibilityChange !== false;
}

export function subscribeBrowserVisibilityRefetch(
	onVisibleOrRestored: () => void,
	options?: BrowserVisibilitySubscribeOptions,
): () => void {
	const onVisibilityChange = (): void => {
		if (document.visibilityState !== "visible") {
			return;
		}
		onVisibleOrRestored();
	};

	const onPageShow = (event: PageTransitionEvent): void => {
		if (event.persisted) {
			onVisibleOrRestored();
		}
	};

	const listenVisibility = shouldListenVisibilityChange(options);
	if (listenVisibility) {
		document.addEventListener("visibilitychange", onVisibilityChange);
	}
	window.addEventListener("pageshow", onPageShow);
	return (): void => {
		if (listenVisibility) {
			document.removeEventListener("visibilitychange", onVisibilityChange);
		}
		window.removeEventListener("pageshow", onPageShow);
	};
}
